import { useImmerReducer } from "use-immer";
import { useEffect } from "react";
import moment from "moment";
import { getActivityLogListApi } from "../api/commonApi";
import { DEFAULT_PAGINATION, errorMessage } from "../../utils";

const reducer = (draft, action) => {
  switch (action.type) {
    case "SET_PAGINATION":
      draft.pagination = action.payload;
      break;
    case "SET_GOTO_PAGE":
      draft.pagination.pageIndex = action.payload;
      break;
    case "SET_PAGE_SIZE":
      draft.pagination.pageIndex = 1;
      draft.pagination.pageSize = action.payload;
      break;
    case "SET_LOADING":
      draft.loading = action.payload;
      break;
    case "SET_ACTIVITY_LOG_LIST":
      draft.activityLogList = action.payload;
      break;
    case "SET_IS_OPEN_ACTIVITY_DIALOG":
      draft.isOpenActivityDialog = action.payload.open;
      draft.currentActivityLogId = action.payload?.activityLogId;
      break;
    case "ON_OPEN_OR_CLOSE_DATE_RANGE_SELECTION":
      draft.filter.date.isCalenderOpen = action.payload;
      break;
    case "SET_DATE_FILTER":
      draft.pagination.pageIndex = 1;
      draft.filter.date.showDate = action.payload;
      draft.filter.date.isCalenderOpen = false;
      draft.filter.appliedFilters = [
        {
          type: "date range",
          label: `${moment(action.payload[0].startDate).format(
            "yyy-MM-DD",
          )} to ${moment(action.payload[0].endDate).format("yyy-MM-DD")}`,
        },
      ];
      break;
    case "SET_DATE_FILTER_SELECTION":
      draft.filter.date.value = action.payload;
      break;
    case "RESET_FILTERS":
      draft.pagination.pageIndex = 1;
      draft.filter = defaultFilterObject;
      break;
    default:
      break;
  }
};

const defaultDateObject = {
  isCalenderOpen: false,
  value: [
    {
      startDate: new Date(),
      endDate: new Date(),
      key: "selection",
    },
  ],
  showDate: [],
};

const defaultFilterObject = {
  date: defaultDateObject,
  appliedFilters: [],
  reset: false,
};

export const useActivityLogTable = ({ subjectType, id }) => {
  const [state, dispatch] = useImmerReducer(reducer, {
    pagination: DEFAULT_PAGINATION,
    loading: true,
    isOpenActivityDialog: false,
    activityLogList: [],
    currentActivityLogId: null,
    filter: defaultFilterObject,
  });

  const getActivityLogList = async (params) => {
    try {
      dispatch({ type: "SET_LOADING", payload: true });
      const response = await getActivityLogListApi(params);

      const { success, data } = response;
      if (success) {
        dispatch({ type: "SET_PAGINATION", payload: data.pagination });
        dispatch({ type: "SET_ACTIVITY_LOG_LIST", payload: data.products });
      } else {
        throw response;
      }
    } catch (error) {
      const message = error.response
        ? error.response.data?.message
        : "Failed to fetch segment activity log list";

      errorMessage(message);
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  };

  const onOpenCloseActivityLogDialog = (activityLogId) => {
    dispatch({
      type: "SET_IS_OPEN_ACTIVITY_DIALOG",
      payload: {
        open: !state.isOpenActivityDialog,
        activityLogId,
      },
    });
  };

  const gotoPage = async (page) => {
    dispatch({ type: "SET_GOTO_PAGE", payload: page });

    const params = await getRequestUtilities({
      ...state,
      pagination: { ...state.pagination, pageIndex: page },
    });

    getActivityLogList(params);
  };

  const setPageSize = async (e) => {
    const newPageSize = +e.target.value;

    dispatch({ type: "SET_PAGE_SIZE", payload: newPageSize });

    const params = await getRequestUtilities({
      ...state,
      pagination: { ...state.pagination, pageIndex: 1, pageSize: newPageSize },
    });

    getActivityLogList(params);
  };

  const onOpenDateRangeSelection = (payload) => {
    dispatch({
      type: "ON_OPEN_OR_CLOSE_DATE_RANGE_SELECTION",
      payload,
    });
  };

  const handleDateChangeApply = async () => {
    dispatch({
      type: "SET_DATE_FILTER",
      payload: state.filter.date.value,
    });
  };

  const onFilterSelection = (event) => {
    dispatch({
      type: "SET_DATE_FILTER_SELECTION",
      payload: event.value,
    });
  };

  const onClearAllFilter = async () => {
    dispatch({
      type: "RESET_FILTERS",
    });

    const params = await getRequestUtilities({
      ...state,
      pagination: { ...state.pagination, pageIndex: 1 },
      filter: defaultFilterObject,
    });

    getActivityLogList(params);
  };

  const getRequestUtilities = async (currentState) => {
    const params = {
      subject_type: subjectType,
      page: currentState.pagination.pageIndex,
      per_page: currentState.pagination.pageSize,
    };
    if(id) params.subject_id = id;

    if (currentState.filter.date.showDate.length > 0) {
      params.start_date = moment(
        currentState.filter.date.showDate[0].startDate,
      ).format("YYYY-MM-DD");
      params.end_date = moment(
        currentState.filter.date.showDate[0].endDate,
      ).format("YYYY-MM-DD");
    }

    return params;
  };

  useEffect(() => {
    getRequestUtilities(state).then((params) => {
      getActivityLogList(params);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (state.filter.date.showDate.length > 0) {
      getRequestUtilities(state).then((params) => {
        getActivityLogList(params);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.filter.date.showDate]);

  return {
    state,
    gotoPage,
    setPageSize,
    dispatch,
    onOpenCloseActivityLogDialog,
    onOpenDateRangeSelection,
    handleDateChangeApply,
    onFilterSelection,
    onClearAllFilter,
  };
};
